<template lang="pug">
div
  //- Fetching
  template(v-if="isFetching && !initialized")
    v-skeleton-loader.py-8.px-4(type="list-item-three-line@5")
  template(v-else)
    //- Title and actions bars
    .text-md.font-semibold.mt-8.mb-4 {{ $t('documents') }}
    //- Data listing
    .data-table
      //- Content
      v-card.round-lg.pa-4(outlined)
        //- Top Tabs
        v-row(no-gutters)
          v-col(cols="8")
            v-tabs(color="basic700" :height="44" show-arrows active-class="font-semibold")
              v-tab.text-sm.font-medium.text-none.px-4 {{ $t('all') }}
          v-col(align="end")
            .d-flex.align-center
              v-spacer
              v-btn(@click="clickSearch" icon color="neutral500")
                v-icon(size="20") $yzi-search-sm
              v-text-field(v-if="showSearchField" v-model="searchText" @blur="blurSearch" solo dense flat placeholder="Type to search..." hide-details clearable autofocus)
        v-divider
        //- Data
        .data-table
          v-data-table.mt-sm-4.pb-6(:headers="headers" :items="patientDocuments" item-key="document.id" :items-per-page="pageSize" :loading="isFetching" hide-default-footer disable-sort loader-height="2")
            //- name
            template(v-slot:item.name="{ item }")
              .text-sm.neutral700--text {{ item.document.name }}
            //- actions
            template(v-slot:item.actions="{ item }")
              .d-flex.justify-end
                v-tooltip(top color="black" content-class="black")
                  template(#activator="{ on, attrs }")
                    v-btn.mr-1(v-bind="attrs" v-on="on" icon small @click="onDownload(item)")
                      v-icon(size=20) $yzi-download-01
                  .text-sm.font-semibold.basic500--text {{ $t('download') }}
                v-menu(offset-y left rounded="lg")
                  template(v-slot:activator="{ on }")
                    v-btn(icon small v-on="on")
                      v-icon mdi-dots-horizontal
                  v-list.py-0
                    v-list-item(@click="onDeleteItem(item)")
                      v-list-item-title.text-sm.danger500--text {{ $t('delete') }}
        div.d-flex.align-center.mb-4(v-if="totalCount > pageSize")
          v-spacer
          div
            yc-pagination-custom(v-model="page" :parentPageSize.sync="pageSize" :count="totalCount" attach menu-top)
      //- Not yet supported
      //- v-col
      //-   v-card.round-lg.pa-8.preview(outlined)
      //-     template(v-if="selectedItem")
      //-       .text-sm.font-semibold.mb-4 {{ $t('file_details') }}
      //-       .text-xs.font-semibold {{ $t('file_type') }}
      //-       .text-xs PDF
      //-     template(v-else)
      //-       .d-flex.align-center.justify-center.fill-height
      //-         .text-sm.neutral500--text No preview data
  .dialogs
    delete-confirmation-dialog(v-if="openDeleteConfirmationDialog" :open.sync="openDeleteConfirmationDialog" :loading="isDeletingItem" @cancel="onCancelDeleteItem" @delete="onConfirmDeleteItem")
      delete-confirmation-details
</template>

<script>
import { DateTime } from 'luxon'
import { mapState, mapGetters, mapActions } from 'vuex'
import { debounce } from '@/utils'
import DeleteConfirmationDialog from '@/components/clinic/dialogs/DeleteConfirmationDialog.vue'
import DeleteConfirmationDetails from '@/components/clinic/invoice/DeleteConfirmationDetails.vue'

export default {
  components: {
    DeleteConfirmationDialog, DeleteConfirmationDetails
  },
  data() {
    return {
      initialized: false,
      searchText: null,
      showSearchField: false,
      isFetching: true,
      patientDocuments: null,
      totalCount: 0,
      pageSize: 20,
      page: 1,
      openDeleteConfirmationDialog: false,
      isDeletingItem: false,
      headers: [
        {
          text: 'Name',
          value: 'name'
        },
        {
          text: 'Upload Date',
          value: 'formatted_date'
        },
        {
          text: '',
          value: 'actions',
        }
      ]
    }
  },
  mounted () {
    this.fetchPatientDocument()
  },
  watch: {
    params() {
      this.fetchPatientDocument()
    }
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapGetters({
      company: 'getCompany'
    }),
    patientId () {
      if (this.$route.params.id) return parseInt(this.$route.params.id)

      return null
    },
    params () {
      return {
        search: this.searchText,
        page: this.page,
        page_size: this.pageSize
      }
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    pageChanged (page) {
      this.page = page
    },
    clickSearch () {
      if (this.showSearchField) {
        if (!this.searchText || this.searchText.length === 0) {
          this.showSearchField = false
        }
      } else {
        this.showSearchField = true 
      }
    },
    blurSearch () {
      if (!this.searchText || this.searchText.length === 0) {
        this.showSearchField = false
      }
    },
    queryChanged: debounce(function () {
      this.fetchPatientDocument()
    }, 750),
    async fetchPatientDocument() {
      try {
        this.isFetching = true
        const { data } = await this.$axios.get(`/clinics/${this.company.slug}/patients/${this.patientId}/documents`, { params: this.params })
        this.patientDocuments = data.results.map(result => this.sanitizeData(result))
        this.totalCount = data.count
        this.isFetching = false
        this.initialized = true
        this.selectedItem = null
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      }
    },
    sanitizeData (data) {
      // Record date
      const formatted_date = data.record_date ? this.displayDate(data.record_date) : null

      return {
        ...data,
        formatted_date
      }
    },
    displayDate (value) {
      if (!value) return null

      const date = DateTime.fromISO(value)
      const formatDate = date.toFormat('dd MMM yyyy')
      const formatTime = date.toFormat('HH:mm a').toLowerCase()

      return `${formatDate}, ${formatTime}`
    },
    async onDownload (item) {
      let key
      try {
        const { data } = await this.$axios.post(`users/${this.user.id}/request_otk`)
        key = data.key
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        return
      }
      let baseUrl = process.env.VUE_APP_BASE_URL
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        baseUrl = 'https://api-test.yezza.io'
      } else if (process.env.VUE_APP_ENV === 'prod') {
        baseUrl = 'https://export.yezza.io'
      }
      const url = `${baseUrl}/clinics/${this.company.slug}/registrations/${item.lead_pid}/preview_document?leadproduct=${item.document.id}&key=${key}`
      window.open(url,'_blank')
    },
    onDeleteItem (item) {
      this.selectedItem = item
      this.openDeleteConfirmationDialog = true
    },
    onCancelDeleteItem () {
      this.selectedItem = null
      this.openDeleteConfirmationDialog = false
    },
    async onConfirmDeleteItem () {
      try {
        this.isDeletingItem = true
        await this.$axios.delete(`/leads/${this.selectedItem.lead_pid}/products/${this.selectedItem.document.id}`)
        this.$emit('updated')
        await this.fetchPatientDocument()
        this.showMessage(['success', this.$t('success_message.successfully_updated')])
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isDeletingItem = false
        this.openDeleteConfirmationDialog = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
</style>